import { boatPathFromProduct } from '../../core/utils/boat-url.util';
import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  OnInit,
  DestroyRef,
  SecurityContext,
  HostListener,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, Location } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { ImageGalleryComponent } from '../../shared/components/image-gallery/image-gallery.component';
import { FullscreenGalleryComponent } from '../../shared/components/fullscreen-gallery/fullscreen-gallery.component';
import { InquiryModalComponent } from '../../shared/components/inquiry-modal/inquiry-modal.component';
import { ProductRatingsComponent } from '../../shared/components/product-ratings/product-ratings.component';
import { SafePipe } from '../../shared/pipes/safe.pipe';
import { UrlService } from '../../core/services/shared/url.service';
import { ListingShareService } from '../../core/services/shared/listing-share.service';
import { SeoService } from '../../core/services/shared/seo.service';
import { normalizeImageUrl } from '../../core/utils/image.util';
import { AnalyticsService } from '../../core/services/shared/analytics.service';
import { environment } from '../../../environments/environment';

interface ProductDetailModel {
  shareImageUrl: string;
  canonicalPath: string;
  listingReference?: string;
  id: number;
  productTypeId: number;
  title: string;
  description: string;
  safeDescription: string;
  descriptionBullets: string[];
  safeDetails: string;
  price: number;
  status: string;
  images: { url: string; mediumUrl: string; thumbUrl: string; type: string }[];
  features: { type: string; features: string[] }[];
  specs: { label: string; value: string }[];
  propulsions: any[];
  contacts: {
    name: string;
    title: string;
    phone: string;
    email: string;
  }[];
  locations: {
    id: number;
    name: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    addressText: string;
    googleMapsUrl: string;
    lat: number | null;
    lng: number | null;
  }[];
  reviews: any[];
  averageRating?: number;
  reviewCount: number;
  videoUrl?: string;
  boatTypeName?: string;
}

@Component({
  selector: 'app-boat-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ImageGalleryComponent,
    FullscreenGalleryComponent,
    InquiryModalComponent,
    ProductRatingsComponent,

    SafePipe,
  ],
  templateUrl: './boat-detail.component.html',
  styleUrls: ['../product-detail/product-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoatDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private destroyRef = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  private seoService = inject(SeoService);
  private urlService = inject(UrlService);
  private listingShare = inject(ListingShareService);
  shareStatus = signal('');
  private platformId = inject(PLATFORM_ID);
  private analytics = inject(AnalyticsService);

  product = signal<ProductDetailModel | null>(null);
  isLoading = signal<boolean>(true);

  isSharePopupOpen = signal<boolean>(false);
  isMerchandise = computed(() => this.product()?.productTypeId !== 1);

  isFullscreenOpen = signal<boolean>(false);
  fullscreenIndex = signal<number>(0);
  isInquiryOpen = signal<boolean>(false);
  inquiryType = signal<'VIEWING' | 'INTEREST'>('INTEREST');
  activeTab = signal<string>('details');
  mainTab = signal<'details' | 'ratings'>('details');

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const target = event.target as HTMLElement;
    const isTrigger = target.closest('.share-trigger-btn');
    const isDropdown = target.closest('.share-dropdown');

    if (!isTrigger && !isDropdown && this.isSharePopupOpen()) {
      this.isSharePopupOpen.set(false);
    }
  }

  ngOnInit(): void {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.seoService.applyRoute(`/boats/${this.route.snapshot.paramMap.get('id')}`);
      const res = data['product'];
      if (!res) {
        this.product.set(null);
        this.isLoading.set(false);
        return;
      }

      // Data normalization (previously inside loadDetails subscribe)
      const normalized = this.normalizeProduct(res, 1);
      if (isPlatformBrowser(this.platformId) && this.location.path().split('?')[0] !== normalized.canonicalPath) {
        const query = this.location.path().includes('?') ? '?' + this.location.path().split('?').slice(1).join('?') : '';
        this.location.replaceState(normalized.canonicalPath + query);
      }
      this.product.set(normalized);
      this.isLoading.set(false);
      this.updateMetaTags(normalized);
      if (['available', 'sale pending'].includes(normalized.status.trim().toLowerCase())) {
        this.analytics.trackPageView(normalized.canonicalPath);
        this.analytics.trackItemView(normalized.id, normalized.title);
      }
    });
  }

  // loadDetails removed as we now use Resolver

  isVideo(url: string | null | undefined): boolean {
    if (!url) return false;
    const cleanUrl = url.toLowerCase().split('?')[0];
    return cleanUrl.endsWith('.mp4');
  }

  private normalizeProduct(res: any, typeId: number): ProductDetailModel {
    const boat = res.boat;
    const merch = res.merchandise;

    const title =
      typeId === 1
        ? boat?.boatName || res.productName || 'Unknown Yacht'
        : res.productName || merch?.merchandiseName || 'Unknown Item';

    // Short Description
    const shortDesc =
      typeId === 1
        ? boat?.boatDescription || res.productDescription || ''
        : res.productDescription || merch?.merchandiseDescription || '';

    const boatTypeName = boat?.boatType?.boatTypeName || 'Yacht';

    // Long Details (bullets) - Safe Parsing Policy per Requirement 2
    const rawLongDetails =
      typeId === 1 ? boat?.boatDetails || '' : merch?.otherDetails || merch?.description || '';

    // Secure formatting and legacy handling
    const formattedDescription = this.formatLegacyText(shortDesc);
    const formattedDetails = this.formatLegacyText(rawLongDetails);

    const safeDescription =
      this.sanitizer.sanitize(SecurityContext.HTML, formattedDescription) || '';
    const safeDetails = this.sanitizer.sanitize(SecurityContext.HTML, formattedDetails) || '';

    let descriptionBullets: string[] = [];
    if (rawLongDetails && !this.hasHtml(rawLongDetails)) {
      // Relaxed bullet parsing: prioritize bullets, fallback to non-empty lines
      const bulletMarkers = ['•', '-', '*'];
      const firstBulletIndex = Math.min(
        ...bulletMarkers.map((m) => rawLongDetails.indexOf(m)).filter((i) => i !== -1),
      );

      if (firstBulletIndex !== Infinity) {
        const afterFirstBullet = rawLongDetails.substring(firstBulletIndex);
        descriptionBullets = afterFirstBullet
          .split(/[•\-*]/)
          .map((x: string) => x.trim())
          .filter((x: string) => x.length > 0);
      } else if (rawLongDetails.includes('\n')) {
        descriptionBullets = rawLongDetails
          .split('\n')
          .map((x: string) => x.trim())
          .filter((x: string) => x.length > 0);
      }
    }

    // Status null safety
    const status = res.status?.statusName ?? 'Available';

    // Images Mapping
    let images: any[] = [];
    const rawPhotos = typeId === 1 ? boat?.boatPhotos : merch?.merchandisePhotos;
    const photos = [...(rawPhotos || [])].sort((a, b) => {
      if (a.isThumbnail === true && b.isThumbnail !== true) return -1;
      if (b.isThumbnail === true && a.isThumbnail !== true) return 1;
      return 0;
    });

    if (photos.length > 0) {
      images = photos.map((p: any) => ({
        url: normalizeImageUrl(p.photoUrl || p.photoMediumUrl || p.photoThumbnailUrl),
        mediumUrl: normalizeImageUrl(p.photoMediumUrl || p.photoUrl || p.photoThumbnailUrl),
        thumbUrl: normalizeImageUrl(p.photoThumbnailUrl || p.photoMediumUrl || p.photoUrl),
        type: this.isVideo(p.photoUrl) ? 'video' : 'image',
      }));
    } else {
      images = [
        {
          url: 'assets/no-image.png',
          mediumUrl: 'assets/no-image.png',
          thumbUrl: 'assets/no-image.png',
          type: 'image',
        },
      ];
    }

    // Feature Mapping
    const featMap: Record<string, string[]> = {};
    const rawFeats = typeId === 1 ? boat?.boatFeatures : merch?.merchandiseFeatures;
    (rawFeats || []).forEach((f: any) => {
      const group = f.feature?.featureType || 'Other';
      if (!featMap[group]) featMap[group] = [];
      featMap[group].push(f.feature?.featureName);
    });
    const features = Object.keys(featMap).map((k) => ({ type: k, features: featMap[k] }));

    // Specs
    const specs: { label: string; value: string }[] = [];
    if (typeId === 1 && boat) {
      if (boat.boatModel) specs.push({ label: 'Model', value: boat.boatModel });
      if (boat.boatYear) specs.push({ label: 'Year', value: boat.boatYear.toString() });
      if (boat.overallLength) specs.push({ label: 'Length', value: boat.overallLength + ' ft' });
      if (boat.beam) specs.push({ label: 'Beam', value: boat.beam + ' ft' });
      if (boat.maxDraft) specs.push({ label: 'Draft', value: boat.maxDraft + ' ft' });
      if (boat.hullMaterial) specs.push({ label: 'Hull Material', value: boat.hullMaterial });
      if (boat.hullShape) specs.push({ label: 'Hull Shape', value: boat.hullShape });
      if (boat.engineHours != null)
        specs.push({ label: 'Engine Hours', value: boat.engineHours.toString() });
      if (boat.totalPower != null)
        specs.push({ label: 'Total Power', value: boat.totalPower + ' HP' });
      if (boat.freshWaterTank)
        specs.push({ label: 'Fresh Water Tank', value: boat.freshWaterTank + ' gal' });
      if (boat.fuelTank) specs.push({ label: 'Fuel Tank', value: boat.fuelTank + ' gal' });
      if (boat.holdingTank) specs.push({ label: 'Holding Tank', value: boat.holdingTank + ' gal' });
      if (boat.guestCabin) specs.push({ label: 'Guest Cabins', value: boat.guestCabin.toString() });
      if (boat.guestHead) specs.push({ label: 'Guest Heads', value: boat.guestHead.toString() });
      if (boat.crewCabin) specs.push({ label: 'Crew Cabins', value: boat.crewCabin.toString() });
      if (boat.crewHead) specs.push({ label: 'Crew Heads', value: boat.crewHead.toString() });
    }

    // Contacts Mapping - Zero Loss Policy
    const contacts = (res.productContacts || []).map((pc: any) => {
      const staff = pc.staff;
      if (!staff) {
        console.warn('[BoatDetail] Missing staff in productContact', pc);
      }
      return {
        name: staff ? `${staff.firstName} ${staff.lastName}` : '',
        title:
          staff?.title ||
          (staff?.roles && staff.roles.length > 0 ? staff.roles[0].roleName : 'Broker'),
        phone: staff?.address?.phone ?? '',
        email: staff?.address?.email ?? '',
      };
    });

    if (contacts.length === 0) {
      contacts.push({
        name: 'League City Yacht Sales Brokerage',
        title: 'Sales & Support',
        phone: '',
        email: '',
      });
    }

    // Locations Mapping - Zero Loss Policy
    const locations = (res.productLocations || []).map((l: any) => {
      const loc = l.location;
      if (!loc) {
        console.warn('[BoatDetail] Missing location data', l);
      }

      let addressParts = [];
      const addr = loc?.address;
      if (addr) {
        if (addr.addressLine1) addressParts.push(addr.addressLine1);
        if (addr.addressLine2) addressParts.push(addr.addressLine2);
        if (addr.city) addressParts.push(addr.city);
        if (addr.state) addressParts.push(addr.state);
        if (addr.zipCode) addressParts.push(addr.zipCode);
        if (addr.country) addressParts.push(addr.country);
        if (addr.phone) addressParts.push(addr.phone);
      }
      const addressText =
        addressParts.length > 0 ? addressParts.join(', ') : 'Location Not Specified';
      const query = encodeURIComponent(
        `${loc?.locationName || 'League City Yacht Sales'}, ${addressText}`,
      );

      return {
        id: loc?.locationId || 0,
        name: loc?.locationName || 'Main Office',
        addressLine1: addr?.addressLine1 || '',
        addressLine2: addr?.addressLine2 || '',
        city: addr?.city || '',
        state: addr?.state || '',
        zipCode: addr?.zipCode || '',
        phone: addr?.phone || '',
        addressText,
        googleMapsUrl:
          addressParts.length > 0
            ? `https://www.google.com/maps/search/?api=1&query=${query}`
            : null,
        lat: addr?.latitude ?? null,
        lng: addr?.longitude ?? null,
      };
    });

    const averageRating =
      res.productReviews && res.productReviews.length > 0
        ? res.productReviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0) /
          res.productReviews.length
        : 0;

    return {
      canonicalPath: boatPathFromProduct(res),
      shareImageUrl: this.urlService.getShareImageUrl(res.productId, res.boatThumbnailPhoto?.id ?? 0),
      listingReference: res.listingReference,
      id: res.productId,
      productTypeId: typeId,
      title,
      description: shortDesc,
      safeDescription,
      descriptionBullets,
      safeDetails,
      price: res.price ?? null,
      status,
      images,
      features,
      specs,
      propulsions: boat?.propulsions || [],
      contacts,
      locations,
      reviews: res.productReviews || [],
      averageRating,
      reviewCount: res.productReviews?.length || 0,
      videoUrl: boat?.videoUrl,
      boatTypeName,
    };
  }

  private updateMetaTags(product: ProductDetailModel): void {
    const currentUrl = new URL(product.canonicalPath, environment.publicUrl).toString();

    // Comprehensive HTML stripping and cleanup
    const cleanDescription = (product.description || '')
      .replace(/<[^>]*>/g, ' ') // Replace tags with space to avoid word merging
      .replace(/&nbsp;/g, ' ') // Handle space entities
      .replace(/\s+/g, ' ') // Collapse multiple spaces
      .trim();

    // Truncate for optimal social preview (most platforms cut off after ~200)
    const previewDescription =
      cleanDescription.length > 200
        ? cleanDescription.substring(0, 197) + '...'
        : cleanDescription || 'View details for this yacht on League City Yacht Sales.';

    this.seoService.setMeta({
      title: `${product.title} | League City Yacht Sales`,
      description: previewDescription,
      image: product.shareImageUrl,
      imageWidth: 1200, imageHeight: 630, imageType: 'image/jpeg',
      url: currentUrl,
    });

    this.seoService.setRobots(['available', 'sale pending'].includes(product.status.trim().toLowerCase()));

    const schema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.title,
      image: product.images && product.images.length > 0 ? product.images[0].url : '',
      description: previewDescription,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: product.price || 0,
        availability: 'https://schema.org/InStock',
        url: currentUrl,
      },
    };
    this.seoService.setSchema(schema);
  }

  private formatLegacyText(text: string): string {
    if (!text) return '';
    if (this.hasHtml(text)) return text;
    return text.replace(/\n/g, '<br>');
  }

  private hasHtml(text: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(text);
  }

  get b(): ProductDetailModel | null {
    return this.product();
  }

  openFullscreen(index: number): void {
    this.fullscreenIndex.set(index);
    this.isFullscreenOpen.set(true);
  }

  closeFullscreen(): void {
    this.isFullscreenOpen.set(false);
  }

  openInquiry(type: 'VIEWING' | 'INTEREST'): void {
    this.inquiryType.set(type);
    this.isInquiryOpen.set(true);
  }

  closeInquiry(): void {
    this.isInquiryOpen.set(false);
  }

  setTab(tab: string): void {
    this.activeTab.set(tab);
  }

  goBack(): void {
    this.location.back();
  }

  shareTo(platform: 'facebook' | 'twitter' | 'whatsapp' | 'instagram'): void {
    const p = this.product();
    if (!p) return;

    const shareUrl = this.urlService.getFrontendShareUrl(p.id, 'yachts');
    const url = encodeURIComponent(shareUrl);
    const title = encodeURIComponent(p.title);

    let finalUrl = '';

    switch (platform) {
      case 'facebook':
        this.shareStatus.set('Copying caption for Facebook...');
        void this.listingShare.facebook(shareUrl, { title: p.title,
          description: p.description, price: p.price, path: p.canonicalPath })
          .then(message => this.shareStatus.set(message));
        return;
      case 'twitter':
        finalUrl = `https://x.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'whatsapp':
        finalUrl = `https://wa.me/?text=${title}%20${url}`;
        break;
      case 'instagram':
        this.copyToClipboard(
          shareUrl,
          'Link copied! Open Instagram to share in your story or bio.',
        );
        return;
    }

    if (finalUrl && isPlatformBrowser(this.platformId)) {
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    }
    this.isSharePopupOpen.set(false);
  }

  shareLink(): void {
    const p = this.product();
    if (!p) return;
    const finalUrl = this.urlService.getFrontendShareUrl(p.id, 'yachts');
    this.copyToClipboard(finalUrl, 'Link copied to clipboard!');
    this.isSharePopupOpen.set(false);
  }

  private copyToClipboard(text: string, message: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => alert(message));
    } else {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      alert(message);
    }
  }

  openMap(lat: number | null, lng: number | null, location?: any): void {
    if (!isPlatformBrowser(this.platformId)) return;

    let targetLat = lat;
    let targetLng = lng;
    let searchQuery = '';

    const locName = location?.name?.toLowerCase() || '';

    if (locName.includes('other')) {
      // Texas default
      searchQuery = 'Texas';
    } else if (locName.includes('international')) {
      // Europe/Asia/Africa default
      searchQuery = 'Tyrrhenian Sea';
    } else if (lat == null || lng == null) {
      if (location?.googleMapsUrl) {
        window.open(location.googleMapsUrl, '_blank', 'noopener,noreferrer');
        return;
      } else {
        return;
      }
    }

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    let url: string;

    if (searchQuery) {
      if (isIOS) {
        url = `maps://maps.apple.com/?q=${encodeURIComponent(searchQuery)}`;
      } else {
        url = `https://www.google.com/maps?q=${encodeURIComponent(searchQuery)}`;
      }
    } else {
      if (isIOS) {
        url = `maps://maps.apple.com/?q=${targetLat},${targetLng}`;
      } else {
        url = `https://www.google.com/maps?q=${targetLat},${targetLng}`;
      }
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
